import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { makeStyles } from '@material-ui/core';
import useRequest from '@hooks/useRequest';
import { DetailedRequestInput } from '@routes/NewRequest/FormComponents';
import DesignReferenceFileInput from '@routes/NewRequest/FormComponents/DesignReferenceFileInput';
import { DesignStepProps, ReferenceRequestForm } from './types';
import { useSelector } from 'react-redux';
import { RootState } from '@modules/index';
import { RfqReferenceFileInput } from '@generated/graphql';

const DesignReferenceStep: React.FC<DesignStepProps> = ({
  setDisabledNextButton,
  nextButtonAction
}) => {
  const referenceFilesDefaultValue = useSelector<
    RootState,
    RfqReferenceFileInput[]
  >((state) => state.request?.referenceFiles ?? []);
  const detailedRequestDefaultValue = useSelector<RootState, string>(
    (state) => state.request?.detailedRequest ?? ''
  );
  const [isValid, setIsValid] = useState<boolean>(false);
  const { onUpdateRfqInfo } = useRequest();
  const { requestContainer } = useDesignRequestStyles();
  const formProps = useForm<ReferenceRequestForm>({
    mode: 'onBlur',
    defaultValues: {
      referenceFiles: referenceFilesDefaultValue,
      detailedRequest: detailedRequestDefaultValue
    }
  });
  const { watch } = formProps;
  const { detailedRequest } = watch();

  const currentFormValue = useRef<ReferenceRequestForm>({
    referenceFiles: referenceFilesDefaultValue,
    detailedRequest: detailedRequestDefaultValue
  });

  const onSubmit = useCallback(() => {
    const currentValue = currentFormValue.current;
    onUpdateRfqInfo({
      ...currentValue
    });
  }, [onUpdateRfqInfo]);

  useEffect(() => {
    currentFormValue.current = {
      referenceFiles: referenceFilesDefaultValue,
      detailedRequest
    };
  }, [referenceFilesDefaultValue, detailedRequest]);

  useEffect(() => {
    nextButtonAction.current.REFERENCE = onSubmit;
  }, [onSubmit, nextButtonAction]);

  useEffect(() => {
    if ((referenceFilesDefaultValue ?? []).length === 0) {
      setIsValid(false);
      return;
    }

    if ((detailedRequest ?? '').length === 0) {
      setIsValid(false);
      return;
    }

    setIsValid(true);
  }, [referenceFilesDefaultValue, detailedRequest]);

  useEffect(() => {
    if (!setDisabledNextButton) return;

    setDisabledNextButton(!isValid);
  }, [isValid, setDisabledNextButton]);

  useEffect(() => {
    return onSubmit;

    //eslint-disable-next-line
  }, []);

  return (
    <div className={requestContainer}>
      <FormProvider {...formProps}>
        <form>
          <DesignReferenceFileInput />
          <DetailedRequestInput />
        </form>
      </FormProvider>
    </div>
  );
};

export default DesignReferenceStep;

const useDesignRequestStyles = makeStyles((theme) => ({
  requestContainer: {
    width: '100%',
    maxWidth: '464px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '72px',
    '& .bom-description': {
      marginTop: 40,
      '& > p': {
        wordBreak: 'break-all'
      }
    },
    [theme.breakpoints.down('sm')]: {
      paddingTop: 0
    }
  }
}));
